<script lang="ts">
  import Clock from '../charts/Clock.svelte';
  import LineChart from '../charts/LineChart.svelte';
  import ChartTooltip from '@ui/ChartTooltip.svelte';
  import type { ClockCell } from '../admin';
  import type { TimingBlock } from '@api';

  // Weekly volume-vs-engagement ("overwhelm") chart + the best-time-to-post clock.
  // Two `/v1/…/insights` blocks, named: `clock` is its own block there, not a field
  // of the content payload, which is what stops the Overview and this page from
  // each computing and shipping their own copy of the same grid (doc 04 §4.3).
  let { timing, clock }: { timing: TimingBlock; clock: ClockCell[] } = $props();

  function corrText(r: number | null | undefined): string {
    if (r == null) return 'not enough weeks to measure';
    if (r <= -0.3) return 'busier weeks see less engagement per post — overwhelm signal';
    if (r >= 0.3) return 'busier weeks also engage more per post — volume is fine';
    return 'no strong link between volume and engagement per post';
  }

  const CA = 'var(--color-primary)'; // posts / week
  const CB = 'var(--color-secondary)'; // comments per post
  const H = 150;

  const weeks = $derived(timing.overwhelm ?? []);
  const overwhelmSeries = $derived([
    { name: 'posts / week', values: weeks.map((w) => w.posts as number | null), color: CA },
    { name: 'comments per post', values: weeks.map((w) => w.comments_per_post), color: CB, dashed: true },
  ]);

  // Per-series maxima (LineChart normalises each series to its own range) so the hover
  // dots land exactly on the drawn lines.
  const n = $derived(weeks.length);
  const postsMax = $derived(Math.max(1e-6, ...weeks.map((w) => w.posts)));
  const commentsMax = $derived(Math.max(1e-6, ...weeks.map((w) => w.comments_per_post ?? 0)));

  function weekLabel(w: string): string {
    return new Date(w + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  let wrapW = $state(0);
  let hover = $state<{ i: number; x: number; y: number } | null>(null);
  function move(e: MouseEvent): void {
    if (n === 0) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const rel = rect.width ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0;
    hover = { i: Math.round(rel * (n - 1)), x: e.clientX, y: e.clientY };
  }
  const gx = $derived(hover && n > 1 ? (hover.i / (n - 1)) * wrapW : 0);
  const postsY = (i: number): number => H - (weeks[i]!.posts / postsMax) * H;
  const commentsY = (i: number): number => H - ((weeks[i]!.comments_per_post ?? 0) / commentsMax) * H;
  const fmt = (v: number | null): string => (v == null ? '—' : v.toLocaleString());
</script>

<div class="grid gap-6 lg:grid-cols-2">
  <section class="rounded-box border hair bg-base-100 p-5">
    <div class="eyebrow mb-1">Overwhelm test · volume vs engagement</div>
    <p class="mb-3 text-xs text-base-content/65">
      Weekly posting volume against comments-per-post, each scaled to its own range. If the
      lines pull apart when volume spikes, the feed is outrunning your members.
    </p>
    {#if weeks.length === 0}
      <p class="text-sm text-base-content/62">Not enough weekly history yet.</p>
    {:else}
      <div class="mb-2 flex flex-wrap items-center gap-3 font-mono text-[0.7rem] text-base-content/65">
        <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-sm" style:background={CA}></span>posts / week</span>
        <span class="flex items-center gap-1"><span class="inline-block h-0.5 w-3" style:background={CB}></span>comments per post</span>
      </div>
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="relative"
        style="height:{H}px"
        bind:clientWidth={wrapW}
        onmousemove={move}
        onmouseenter={move}
        onmouseleave={() => (hover = null)}
      >
        <LineChart series={overwhelmSeries} height={H} normalize legend={false} />
        {#if hover}
          <div class="pointer-events-none absolute inset-y-0 w-px bg-base-content/25" style="left:{gx}px"></div>
          <div class="pointer-events-none absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style="left:{gx}px; top:{postsY(hover.i)}px; background:{CA}"></div>
          <div class="pointer-events-none absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style="left:{gx}px; top:{commentsY(hover.i)}px; background:{CB}"></div>
        {/if}
      </div>
      <div class="mt-2 flex justify-between font-mono text-[0.6rem] text-base-content/62">
        <span>{weeks[0] ? weekLabel(weeks[0].week) : ''}</span><span>{weeks.at(-1) ? weekLabel(weeks.at(-1)!.week) : ''}</span>
      </div>
      <p class="mt-2 font-mono text-[0.7rem] text-base-content/65">
        correlation r = {timing.overwhelm_corr == null ? '—' : timing.overwhelm_corr.toFixed(2)} · {corrText(timing.overwhelm_corr)}
      </p>
    {/if}
  </section>

  <section class="rounded-box border hair bg-base-100 p-5">
    <div class="eyebrow mb-3">Best time to post · weekday × hour (UTC)</div>
    {#if clock.length === 0}
      <p class="text-sm text-base-content/62">No activity recorded yet.</p>
    {:else}
      <Clock cells={clock} />
    {/if}
  </section>
</div>

{#if hover && weeks[hover.i]}
  {@const w = weeks[hover.i]!}
  <ChartTooltip x={hover.x} y={hover.y}>
    <div class="font-mono text-[0.7rem] font-semibold">Week of {weekLabel(w.week)}</div>
    <div class="mt-0.5 text-xs text-base-content/70">
      <span class="font-semibold" style:color={CA}>{fmt(w.posts)}</span> posts ·
      <span class="font-semibold" style:color={CB}>{fmt(w.comments_per_post)}</span> comments each
    </div>
    <div class="mt-0.5 font-mono text-[0.62rem] text-base-content/68">▲ {fmt(w.upvotes_per_post)} upvotes per post</div>
  </ChartTooltip>
{/if}
